import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class VesselCapability extends ModelObjectWithDirtyCheck<VesselCapability> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Vessel Capability Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    public isHidden: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.isHidden = jsonSerializedObject.isHidden;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.isHidden = this.isHidden;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "VesselCapability.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
    ];
}


export class VesselCapabilityService extends ServiceBase {

  public async getVesselCapabilities(vesselCapabilityIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(VesselCapability)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    vesselCapabilityIds:vesselCapabilityIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.vesselCapabilities.map((v: any) => new VesselCapability(v));;
    return this.makeJsonRequest("vesselCapability/v1/getVesselCapabilities", req, false, extractor, opts);
  };

  public async getAllVesselCapabilities(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(VesselCapability)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.vesselCapabilities.map((v: any) => new VesselCapability(v));;
    return this.makeJsonRequest("vesselCapability/v1/getAllVesselCapabilities", req, false, extractor, opts);
  };

  public async saveVesselCapability(vesselCapability: VesselCapability, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    vesselCapability:vesselCapability.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("vesselCapability/v1/saveVesselCapability", req, true, extractor, opts);
  };

  public async setVesselCapabilityIsHidden(vesselCapabilityId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    vesselCapabilityId:vesselCapabilityId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("vesselCapability/v1/setVesselCapabilityIsHidden", req, true, extractor, opts);
  };
}

export const vesselCapabilityService = new VesselCapabilityService();